import { useEffect, useMemo, useRef, useState } from "react";
import { SEO } from "@/components/seo/SEO";
import { generateBreadcrumbSchema, generateMedicalWebPageSchema, generateEducationalApplicationSchema } from "@/lib/seoSchemas";
import { TopicSelect } from "@/components/filters/TopicSelect";
import { useContinueLearning } from "@/hooks/useContinueLearning";
import { TagMultiSelect } from "@/components/filters/TagMultiSelect";
import { DifficultySelect } from "@/components/filters/DifficultySelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { ModernMCQCard, type MCQ } from "@/components/practice/ModernMCQCard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useToast } from "@/components/ui/use-toast";
import { Bookmark, BookmarkCheck, Target, CheckCircle, FileText, BookOpen, RotateCcw, ChevronLeft, ChevronRight, Grid3X3 } from "lucide-react";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useExamContext } from "@/contexts/ExamContext";
import { ExamSwitcher } from "@/components/dashboard/ExamSwitcher";
import { MCQNavigationPalette } from "@/components/navigation/MCQNavigationPalette";
import { FloatingNavigationBar } from "@/components/navigation/FloatingNavigationBar";
import { useQuestionStatus } from "@/hooks/useQuestionStatus";
import { useAdvancedGestures } from "@/hooks/useAdvancedGestures";
import { useNavigationKeyboard } from "@/hooks/useNavigationKeyboard";
import { useSearchParams } from "react-router-dom";
import { GestureFeedback } from "@/components/mobile/GestureFeedback";
import { getGestureConfig } from "@/components/settings/GestureSettings";
import { MobileMCQCard } from "@/components/practice/MobileMCQCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { TopicBreadcrumb } from "@/components/navigation/TopicBreadcrumb";
import { UniversalBreadcrumb } from "@/components/navigation/UniversalBreadcrumb";
import { TopicSwitcher } from "@/components/navigation/TopicSwitcher";
import { SmartBreadcrumb } from "@/components/navigation/SmartBreadcrumb";
import { SmartNextSteps } from "@/components/navigation/SmartNextSteps";
import { useSpacedRepetition } from "@/hooks/useSpacedRepetition";
import { useQuizSession } from "@/hooks/useQuizSession";

const sb = supabase as unknown as SupabaseClient<any>;

const Practice = () => {
  const { updateLearningState } = useContinueLearning();
  const [topicId, setTopicId] = useState<string | undefined>();
  const [difficulty, setDifficulty] = useState<string | undefined>();
  const [tags, setTags] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showPalette, setShowPalette] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const pageSize = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const gestureConfig = getGestureConfig();
  
  // Session tracking for study plan
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [sessionMcqCount, setSessionMcqCount] = useState(0);
  const [sessionCorrectCount, setSessionCorrectCount] = useState(0);
  
  const { isAdmin } = useEntitlement();
  const { selectedExamSlug, setSelectedExamSlug } = useExamContext();
  const { dueCount, dueMcqIds, scheduleReview, completeReview } = useSpacedRepetition();
  const [isReviewMode, setIsReviewMode] = useState(false);
  const resumeId = searchParams.get("resume");
  const sessionMode = (searchParams.get("mode") === "test" ? "test" : "tutor") as "tutor" | "test";
  
  // Initialize filters from URL params
  useEffect(() => {
    const urlTopicId = searchParams.get('topic');
    const urlDifficulty = searchParams.get('difficulty');
    const urlTags = searchParams.get('tags');
    const urlExam = searchParams.get('exam');
    const urlMode = searchParams.get('mode');
    
    if (urlTopicId) setTopicId(urlTopicId);
    if (urlDifficulty) setDifficulty(urlDifficulty);
    if (urlTags) setTags(urlTags.split(','));
    if (urlMode === 'review') setIsReviewMode(true);
    
    // Update ExamContext if exam param exists and differs
    if (urlExam && urlExam !== selectedExamSlug) {
      setSelectedExamSlug(urlExam);
    }
  }, [searchParams, selectedExamSlug, setSelectedExamSlug]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await sb.from("topics").select("id,name").order("name", { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });


  // Get labelId for the selected exam
  const { data: selectedLabelRow } = useQuery({
    queryKey: ["exam-label-for-practice", selectedExamSlug],
    enabled: !!selectedExamSlug,
    queryFn: async () => {
      if (!selectedExamSlug) return null;
      const { data, error } = await sb
        .from("exam_labels")
        .select("id")
        .eq("slug", selectedExamSlug)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
  });

  const labelId = selectedExamSlug ? selectedLabelRow : null;

  // Get topic IDs that have MCQs for the selected exam
  const { data: examTopicIds } = useQuery({
    queryKey: ["exam-topic-ids", labelId, selectedExamSlug],
    enabled: !!selectedExamSlug && !!labelId,
    queryFn: async () => {
      if (!labelId) return null;
      const { data: labelMcqs, error: labelErr } = await sb
        .from("mcq_exam_labels")
        .select("mcq_id")
        .eq("label_id", labelId);
      if (labelErr) throw labelErr;
      const mcqIds = (labelMcqs || []).map((r: any) => r.mcq_id);
      if (mcqIds.length === 0) return new Set<string>();
      const { data: mcqs, error: mcqErr } = await sb
        .from("mcqs")
        .select("topic_id")
        .in("id", mcqIds);
      if (mcqErr) throw mcqErr;
      return new Set((mcqs || []).map((m: any) => m.topic_id).filter(Boolean));
    },
  });

  // Filter topics to only show exam-relevant ones
  const filteredTopicsForSelect = useMemo(() => {
    if (!selectedExamSlug || !examTopicIds) return topics;
    return topics.filter(t => examTopicIds.has(t.id));
  }, [topics, examTopicIds, selectedExamSlug]);

  const { data: mcqData = { rows: [], count: 0 } } = useQuery({
    queryKey: ["mcqs", topicId, difficulty, tags, page, pageSize, labelId, isAdmin, selectedExamSlug],
    queryFn: async () => {
      let q = sb
        .from("mcqs")
        .select("id, topic_id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation, difficulty, tags, stem_image_url, explanation_image_url", { count: "exact" })
        .eq("lifecycle_status", "active")
        .order("created_at", { ascending: false });

      // Admin bypass: skip entitlement gating for admin users
      if (!isAdmin) {
        // Entitlement gating for regular users
        if (selectedExamSlug && labelId) {
          // User selected a specific exam - filter by that exam's label
          const { data: mapRows, error: mapErr } = await sb
            .from("mcq_exam_labels")
            .select("mcq_id")
            .eq("label_id", labelId);
          if (mapErr) throw mapErr;
          const ids = (mapRows || []).map((r: any) => r.mcq_id as string);
          if (ids.length === 0) return { rows: [], count: 0 };
          q = q.in("id", ids);
        } else if (selectedExamSlug === null) {
          // User selected "All Exams" - show all unlabeled MCQs (fallback behavior)
          const { data: mapRows2, error: mapErr2 } = await sb.from("mcq_exam_labels").select("mcq_id");
          if (mapErr2) throw mapErr2;
          const ids2 = (mapRows2 || []).map((r: any) => r.mcq_id as string);
          if (ids2.length > 0) {
            const inList = `(${ids2.map((id: string) => `"${id}"`).join(",")})`;
            q = q.not("id", "in", inList);
          }
        } else {
          // No exam selected - show unlabeled MCQs (fallback)
          const { data: mapRows2, error: mapErr2 } = await sb.from("mcq_exam_labels").select("mcq_id");
          if (mapErr2) throw mapErr2;
          const ids2 = (mapRows2 || []).map((r: any) => r.mcq_id as string);
          if (ids2.length > 0) {
            const inList = `(${ids2.map((id: string) => `"${id}"`).join(",")})`;
            q = q.not("id", "in", inList);
          }
        }
      }

      // Hierarchical topic filtering: include parent + all child topics
      if (topicId) {
        const { data: childTopics, error: childError } = await sb
          .from("topics")
          .select("id")
          .eq("parent_topic_id", topicId);
        
        if (childError) throw childError;
        
        const topicIds = [topicId, ...(childTopics || []).map(t => t.id)];
        q = q.in("topic_id", topicIds);
      }
      if (difficulty) q = q.eq("difficulty", Number(difficulty));
      if (tags.length) q = q.contains("tags", tags);
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      const { data, count, error } = await q.range(from, to);
      if (error) throw error;
      
      // Transform database format to frontend format
      const transformedRows = (data || []).map(row => ({
        id: row.id,
        topic_id: row.topic_id,
        question: row.stem,
        options: [
          row.option_a,
          row.option_b,
          row.option_c,
          row.option_d,
          row.option_e
        ].filter(Boolean),
        correct_index: ['A', 'B', 'C', 'D', 'E'].indexOf(row.correct_option),
        explanation: row.explanation,
        difficulty: row.difficulty,
        tags: row.tags,
        stem_image_url: (row as any).stem_image_url,
        explanation_image_url: (row as any).explanation_image_url,
      }));
      
      return { rows: transformedRows, count: count ?? 0 };
    },
  });

  // Spaced repetition review mode query
  const { data: reviewMcqData = { rows: [], count: 0 } } = useQuery({
    queryKey: ["review-mcqs", dueMcqIds],
    enabled: isReviewMode && dueMcqIds.length > 0,
    queryFn: async () => {
      const { data, error } = await sb
        .from("mcqs")
        .select("id, topic_id, stem, option_a, option_b, option_c, option_d, option_e, correct_option, explanation, difficulty, tags")
        .eq("lifecycle_status", "active")
        .in("id", dueMcqIds);
      if (error) throw error;
      const transformedRows = (data || []).map((row: any) => ({
        id: row.id,
        topic_id: row.topic_id,
        question: row.stem,
        options: [row.option_a, row.option_b, row.option_c, row.option_d, row.option_e].filter(Boolean),
        correct_index: ['A', 'B', 'C', 'D', 'E'].indexOf(row.correct_option),
        explanation: row.explanation,
        difficulty: row.difficulty,
        tags: row.tags,
      }));
      return { rows: transformedRows, count: transformedRows.length };
    },
  });

  const { data: tagRows = [] } = useQuery({
    queryKey: ["mcq-tags"],
    queryFn: async () => {
      let allTagRows: { tags: string[] | null }[] = [];
      let offset = 0;
      const limit = 1000;

      while (true) {
        const { data, error } = await sb.from("mcqs").select("tags").range(offset, offset + limit - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        
        allTagRows = [...allTagRows, ...data];
        
        if (data.length < limit) break;
        offset += limit;
      }
      
      return allTagRows as { tags: string[] | null }[];
    },
  });

  const allTags = useMemo(() => {
    const set = new Set<string>();
    (tagRows || []).forEach((r) => (r.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [tagRows]);

  const activeMcqData = isReviewMode ? reviewMcqData : mcqData;

  const questions: MCQ[] = useMemo(() => {
    return (activeMcqData.rows || []).map((q: any) => ({
      id: q.id,
      stem: q.question,
      options: q.options || [],
      correctIndex: q.correct_index,
      explanation: q.explanation || undefined,
      tags: q.tags || [],
      difficulty: q.difficulty,
      stemImageUrl: q.stem_image_url ?? null,
      explanationImageUrl: q.explanation_image_url ?? null,
    }));
  }, [activeMcqData]);

const mcqIds = useMemo(() => (mcqData.rows || []).map((r: any) => r.id as string), [mcqData]);

const { data: bookmarks = [] } = useQuery({
  queryKey: ["bookmarks", userId, mcqIds],
  enabled: !!userId && (mcqIds?.length ?? 0) > 0,
  queryFn: async () => {
    const { data, error } = await sb
      .from("user_bookmarks")
      .select("mcq_id")
      .in("mcq_id", mcqIds as string[]);
    if (error) throw error;
    return (data as { mcq_id: string }[]).map((d) => d.mcq_id);
  },
});

const bookmarkedSet = useMemo(() => new Set<string>(bookmarks), [bookmarks]);

// Fetch attempted MCQ IDs for progress calculation
const { data: attemptedMcqIds = [] } = useQuery({
  queryKey: ["attempted-mcqs", userId, mcqIds],
  enabled: !!userId && mcqIds.length > 0,
  queryFn: async () => {
    const { data, error } = await sb
      .from("attempts")
      .select("mcq_id")
      .eq("user_id", userId)
      .in("mcq_id", mcqIds);
    
    if (error) throw error;
    return [...new Set((data || []).map(d => d.mcq_id))];
  }
});

// Question status management
const {
  questionStatuses,
  toggleFlag,
  markAnswered,
  updateBookmark,
  getQuestionStatus,
  stats,
  resetQuestionStatus,
  clearAnswer
} = useQuestionStatus(questions.length);

// Update bookmark status when bookmarks change
useEffect(() => {
  questions.forEach((q, i) => {
    const isBookmarked = bookmarkedSet.has(q.id);
    if (getQuestionStatus(i).bookmarked !== isBookmarked) {
      updateBookmark(i, isBookmarked);
    }
  });
}, [bookmarkedSet, questions, updateBookmark, getQuestionStatus]);

const totalCount = mcqData.count ?? 0;
const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
const fromItem = totalCount ? (page - 1) * pageSize + 1 : 0;
const toItem = totalCount ? Math.min(page * pageSize, totalCount) : 0;

  useEffect(() => {
    setPage(1);
    setIndex(0);
    setCorrectCount(0);
    resetQuestionStatus();
    setSessionStartTime(null);
    setSessionMcqCount(0);
    setSessionCorrectCount(0);
  }, [topicId, difficulty, tags, resetQuestionStatus]);
  
  // Start session tracking when questions load
  useEffect(() => {
    if (!sessionStartTime && questions.length > 0) {
      setSessionStartTime(new Date());
    }
  }, [questions.length, sessionStartTime]);

const checkAndUpdateStudyPlanTask = async () => {
  if (!topicId || !sessionStartTime || sessionMcqCount === 0 || !userId) return;
  
  const durationMinutes = Math.floor((new Date().getTime() - sessionStartTime.getTime()) / 60000);
  
  try {
    const { data, error } = await supabase.functions.invoke('update-task-progress', {
      body: {
        activityType: 'practice_mcqs',
        topicId,
        sessionDate: new Date().toISOString().split('T')[0],
        durationMinutes,
        mcqCount: sessionMcqCount,
        correctCount: sessionCorrectCount
      }
    });
    
    if (!error && data?.taskCompleted) {
      toast({
        title: "Study Plan Updated! 🎯",
        description: `Completed: ${data.taskName}. Week progress: ${data.weekProgress}%`,
        duration: 5000
      });
    }
  } catch (err) {
    console.error('Error updating study plan task:', err);
  }
};

const onSubmit = (selected: number, ok: boolean, confidence?: number | null) => {
  if (ok) {
    setCorrectCount((c) => c + 1);
    setSessionCorrectCount((c) => c + 1);
  }
  setSessionMcqCount((c) => c + 1);
  markAnswered(index, selected, ok);
  
  const q = questions[index];
  if (!q || !userId) return;

  // Spaced repetition: schedule incorrect answers, update review on correct
  if (!ok) {
    scheduleReview(q.id);
  } else if (isReviewMode) {
    completeReview({ mcqId: q.id, wasCorrect: true });
  }

  sb
    .from("attempts")
    .insert({ user_id: userId, mcq_id: q.id, is_correct: ok, confidence: confidence ?? null })
    .then(({ error }: any) => {
      if (error) {
        toast({ title: "Could not save attempt", description: error.message, variant: "destructive" });
      } else {
        queryClient.invalidateQueries({ queryKey: ["attempted-mcqs", userId] });
        queryClient.invalidateQueries({ queryKey: ["user-topic-progress", userId] });
        checkAndUpdateStudyPlanTask();
      }
    });
};

const toggleBookmark = async (mcqId: string) => {
  if (!userId) return;
  if (bookmarkedSet.has(mcqId)) {
    const { error } = await sb.from("user_bookmarks").delete().eq("user_id", userId).eq("mcq_id", mcqId);
    if (error) {
      toast({ title: "Failed to remove bookmark", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Removed bookmark" });
      queryClient.invalidateQueries({ queryKey: ["bookmarks", userId] });
    }
  } else {
    const { error } = await sb.from("user_bookmarks").insert({ user_id: userId, mcq_id: mcqId });
    if (error) {
      toast({ title: "Failed to bookmark", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Bookmarked" });
      queryClient.invalidateQueries({ queryKey: ["bookmarks", userId] });
    }
  }
};

// Quiz session: persist progress + support resume
const { resumed, persistIndex } = useQuizSession({
  resumeId,
  mcqIds: questions.map((q) => q.id),
  mode: sessionMode,
  topicId,
});

// When a resumed session loads, jump to the saved index (once)
const resumedAppliedRef = useRef(false);
useEffect(() => {
  if (resumed && !resumedAppliedRef.current && questions.length > 0) {
    const targetIdx = Math.min(resumed.current_index ?? 0, questions.length - 1);
    setIndex(targetIdx);
    resumedAppliedRef.current = true;
  }
}, [resumed, questions.length]);

// Navigation functions (persist index for resume)
const goToPrevious = () => setIndex((i) => { const n = Math.max(0, i - 1); persistIndex(n); return n; });
const goToNext = () => setIndex((i) => { const n = Math.min(i + 1, questions.length - 1); persistIndex(n); return n; });
const goToQuestion = (questionIndex: number) => { setIndex(questionIndex); persistIndex(questionIndex); };

// Update learning state when index or topic changes
useEffect(() => {
  if (questions.length > 0) {
    const currentTopic = topics.find(t => t.id === topicId);
    updateLearningState({
      path: `/practice${topicId ? `?topic=${topicId}` : ''}`,
      type: 'practice',
      title: 'Practice MCQs',
      position: index,
      topicId,
      topicName: currentTopic?.name,
    });
  }
}, [index, topicId, questions.length, topics, updateLearningState]);

const handleFlag = () => {
  toggleFlag(index);
  toast({ 
    title: getQuestionStatus(index).flagged ? "Question unflagged" : "Question flagged for review",
    variant: "default"
  });
};

const handleBookmark = () => {
  if (!questions[index] || !userId) {
    toast({ title: "Sign in required", description: "Log in to bookmark questions." });
    return;
  }
  toggleBookmark(questions[index].id);
};

const handleReset = () => {
  setIndex(0);
  setCorrectCount(0);
  resetQuestionStatus();
};

const handleClearAnswer = () => {
  const wasCorrect = getQuestionStatus(index).correct;
  clearAnswer(index);
  if (wasCorrect) {
    setCorrectCount((c) => Math.max(0, c - 1));
  }
  toast({ title: "Answer cleared", description: "You can re-attempt this question." });
};

// Advanced gesture navigation
const { feedback } = useAdvancedGestures({
  onSwipeLeft: goToNext,
  onSwipeRight: goToPrevious,
  onSwipeUp: () => {
    // Show explanation if answered
    if (getQuestionStatus(index).answered && questions[index]?.explanation) {
      // Trigger explanation expand
    }
  },
  onSwipeDown: () => {
    // Hide explanation
  },
  onLongPress: handleBookmark,
  onDoubleTap: gestureConfig.doubleTapAction === "bookmark" ? handleBookmark : 
                gestureConfig.doubleTapAction === "flag" ? handleFlag : undefined,
  swipeThreshold: gestureConfig.swipeThreshold,
  longPressDelay: gestureConfig.longPressDelay,
  hapticFeedback: gestureConfig.hapticFeedback,
  enabled: gestureConfig.enabled && isMobile
});

// Keyboard navigation (desktop)
useNavigationKeyboard({
  onNext: goToNext,
  onPrevious: goToPrevious,
  onTogglePalette: () => setShowPalette(!showPalette),
  onFlag: handleFlag,
  onBookmark: handleBookmark,
  onReset: handleReset,
  enabled: !isMobile
});

const attemptedCount = userId ? attemptedMcqIds.length : 0;
const progress = questions.length > 0 ? (attemptedCount / questions.length) * 100 : 0;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  
  const schemas = [
    generateBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Practice", path: "/practice" }
    ], origin),
    generateMedicalWebPageSchema(
      "Medical MCQ Practice",
      "Interactive medical question bank with thousands of MCQs covering all medical specialties. Practice with detailed explanations and track your progress.",
      `${origin}/practice`
    ),
    generateEducationalApplicationSchema(
      "Medical MCQ Practice Platform",
      "Comprehensive medical exam preparation with practice questions, detailed explanations, and progress tracking for medical students.",
      `${origin}/practice`
    )
  ];

  return (
    <div className={`space-y-6 pb-24 animate-fade-in ${isFullScreen ? 'fixed inset-0 bg-background z-50 overflow-y-auto p-6' : ''}`}>
      <SEO 
        title="Medical MCQ Practice - Interactive Question Bank for Medical Students"
        description="Practice medical MCQs with detailed explanations. Filter by specialty, difficulty, and topic. Track progress and improve exam performance. Free practice questions available."
        path="/practice"
        keywords="medical mcqs, medical practice questions, medical exam prep, medical student questions, usmle practice, neet practice, medical quiz"
        schema={schemas}
      />
      
      {/* Gesture Feedback */}
      <GestureFeedback direction={feedback.direction} action={feedback.action} />
      
      {/* Smart Breadcrumb Navigation */}
      <SmartBreadcrumb 
        currentPath="/practice"
        currentTopicId={topicId}
        currentMode="practice"
      />
      
      {/* Review Mode Banner */}
      {isReviewMode && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RotateCcw className="h-5 w-5 text-orange-600" />
              <div>
                <div className="font-semibold text-foreground">Spaced Repetition Review</div>
                <div className="text-sm text-muted-foreground">
                  {dueMcqIds.length} question{dueMcqIds.length !== 1 ? 's' : ''} due for review
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setIsReviewMode(false); setIndex(0); }}>
              Exit Review
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold gradient-text">
                {isReviewMode ? 'Review Mode' : 'Practice Mode'}
              </h1>
              <ExamSwitcher />
              {!isReviewMode && dueCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  onClick={() => { setIsReviewMode(true); setIndex(0); }}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  {dueCount} Due for Review
                </Button>
              )}
              {isAdmin && (
                <Badge variant="secondary" className="medical-badge text-xs">
                  Admin Access
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {isReviewMode
                ? "Review previously incorrect questions at optimal spaced intervals."
                : "Master medical concepts with interactive MCQs and detailed explanations."}
              {isAdmin && !isReviewMode && " You have access to all MCQs regardless of exam labels."}
            </p>
          </div>
          
          {/* Topic Switcher */}
          <div className="hidden md:block">
            <TopicSwitcher
              currentTopicId={topicId}
              onTopicChange={(newTopicId) => {
                setTopicId(newTopicId);
                if (newTopicId) {
                  setSearchParams({ topic: newTopicId });
                } else {
                  setSearchParams({});
                }
              }}
              contentType="mcq"
            />
          </div>
        </div>
        
        {/* Mobile Topic Switcher */}
        <div className="md:hidden">
          <TopicSwitcher
            currentTopicId={topicId}
            onTopicChange={(newTopicId) => {
              setTopicId(newTopicId);
              if (newTopicId) {
                setSearchParams({ topic: newTopicId });
              } else {
                setSearchParams({});
              }
            }}
            contentType="mcq"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="medical-card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Filter Questions
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <TopicSelect topics={filteredTopicsForSelect} value={topicId} onChange={setTopicId} />
          <DifficultySelect value={difficulty} onChange={setDifficulty} />
          <TagMultiSelect allTags={allTags} value={tags} onChange={setTags} />
          <Button 
            variant="outline" 
            onClick={() => { setPage(1); setIndex(0); setCorrectCount(0); }}
            className="hover-lift"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            variant="default"
            onClick={() => window.location.href = '/quiz-builder'}
            className="hover-lift bg-gradient-medical"
          >
            <Target className="h-4 w-4 mr-2" />
            Custom Quiz Builder
          </Button>
        </div>
      </div>

      {/* Progress & Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="medical-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Attempted</span>
          </div>
          <div className="medical-stat">{attemptedCount}</div>
          <p className="text-xs text-muted-foreground">
            {questions.length > 0 ? Math.round(progress) : 0}% of {questions.length}
          </p>
        </div>
        
        <div className="medical-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium">Correct</span>
          </div>
          <div className="medical-stat text-accent">{stats.correct}</div>
          <p className="text-xs text-muted-foreground">
            {stats.answered > 0 ? Math.round((stats.correct / stats.answered) * 100) : 0}% accuracy
          </p>
        </div>
        
        <div className="medical-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Available</span>
          </div>
          <div className="medical-stat">{totalCount}</div>
          <p className="text-xs text-muted-foreground">
            Showing {fromItem}-{toItem}
          </p>
        </div>
        
        <div className="medical-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bookmark className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Bookmarks</span>
          </div>
          <div className="medical-stat">{bookmarks.length}</div>
          <p className="text-xs text-muted-foreground">Saved questions</p>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage((p) => Math.max(1, p - 1))} 
            disabled={page <= 1}
            className="hover-lift"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button 
            size="sm" 
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))} 
            disabled={page >= totalPages}
            className="bg-gradient-medical hover-lift"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {questions.length === 0 && !mcqData.count && (
        <Card className="medical-card">
          <CardContent className="py-12 text-center space-y-4">
            <div className="flex justify-center">
              <FileText className="h-16 w-16 text-muted-foreground/50" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">No MCQs Available</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {selectedExamSlug 
                  ? `No questions found for the selected exam. Try selecting a different exam or adjust your filters.`
                  : `No questions available. Please select an exam or adjust your filters.`
                }
              </p>
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button variant="outline" onClick={() => window.location.href = '/settings'}>
                Manage Subscriptions
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      

      {/* Question Display */}
      {questions.length ? (
        <div className="space-y-4">
          {/* Question Palette */}
          {showPalette && (
            <div className="fixed top-4 right-4 z-40 animate-fade-in">
              <MCQNavigationPalette
                totalQuestions={questions.length}
                currentQuestion={index}
                questionStatuses={questionStatuses}
                onQuestionSelect={goToQuestion}
              />
            </div>
          )}
          
          {/* Compact palette for mobile */}
          <div className="md:hidden">
            <MCQNavigationPalette
              totalQuestions={questions.length}
              currentQuestion={index}
              questionStatuses={questionStatuses}
              onQuestionSelect={goToQuestion}
              compact
            />
          </div>
          
          <div className="flex items-center justify-between p-4 medical-card">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="medical-badge">
                Question {index + 1} of {questions.length}
              </Badge>
              <Badge 
                variant="secondary" 
                className={`medical-badge ${
                  bookmarkedSet.has(questions[index].id) ? 'medical-badge-success' : ''
                }`}
              >
                {questions[index].tags?.[0] || 'General'}
              </Badge>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPalette(!showPalette)}
              className={`hover-lift md:flex hidden ${showPalette ? 'bg-primary/10 text-primary' : ''}`}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              {showPalette ? "Hide Palette" : "Show Palette"}
            </Button>
          </div>
          
          {isMobile ? (
            <MobileMCQCard 
              mcq={questions[index]} 
              onSubmit={onSubmit}
              onClear={handleClearAnswer}
              mode="practice"
              onFlag={handleFlag}
              onBookmark={handleBookmark}
              isFlagged={getQuestionStatus(index).flagged}
              isBookmarked={bookmarkedSet.has(questions[index]?.id || '')}
              isFullScreen={isFullScreen}
              onToggleFullScreen={() => setIsFullScreen(!isFullScreen)}
            />
          ) : (
            <ModernMCQCard 
              mcq={questions[index]} 
              onSubmit={onSubmit}
              onClearAnswer={handleClearAnswer}
              mode="practice"
            />
          )}
          
          {/* Floating Navigation Bar */}
          <FloatingNavigationBar
            currentQuestion={index}
            totalQuestions={questions.length}
            progress={progress}
            canGoPrevious={index > 0}
            canGoNext={index < questions.length - 1}
            isFlagged={getQuestionStatus(index).flagged}
            isBookmarked={bookmarkedSet.has(questions[index]?.id || '')}
            showPalette={showPalette}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onFlag={handleFlag}
            onBookmark={handleBookmark}
            onTogglePalette={() => setShowPalette(!showPalette)}
            onReset={handleReset}
          />
        </div>
      ) : (
        <div className="medical-card p-12 text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No questions found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your filters to find more questions
          </p>
          <Button 
            onClick={() => { setTopicId(undefined); setDifficulty(undefined); setTags([]); }}
            className="bg-gradient-medical"
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Smart Next Steps */}
      {questions.length > 0 && correctCount > 0 && (
        <div className="mt-8">
          <SmartNextSteps />
        </div>
      )}
    </div>
  );
};

export default Practice;
